import { Stack, Typography } from '@mui/material'
import React, { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import { Header } from 'components/navigation/Header'
import { StatusCheckBreadcrumbs } from 'components/status-checks/common/StatusCheckBreadcrumbs'
import { CreateStatusCheckForm } from 'components/status-checks/create/CreateStatusCheckForm'
import { EditStatusCheckFormContainer } from 'components/status-checks/edit/EditStatusCheckFormContainer'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { useTranslation } from 'hooks/use-translation'
import { UserDefinedService } from 'types/api/services/service'
import {
  ServiceStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'
import { formatExistingStatusCheck } from 'utils/team-external-integrations/team-external-integrations-util'

// The statusCheck passed into location can be of either type ServiceStatusCheck or StatusCheck
// See note in `StatusChecksContext` about supporting both types
// TODO: EN-6581
type LocationState = {
  referrer?: string
  service?: UserDefinedService
  statusCheck?: StatusCheck | ServiceStatusCheck
}

// Type guarding to first check which type of statusCheck was passed in
function shouldFormatStatusCheck(
  statusCheckFromLocationState: StatusCheck | ServiceStatusCheck
): statusCheckFromLocationState is StatusCheck {
  // Chose a field that is required and exists only in StatusCheck and not ServiceStatusCheck
  // We can't choose `identifier` because that gets overwritten in the cloning flow
  return Boolean((statusCheckFromLocationState as StatusCheck)?.createdAt)
}

export const CreateStatusCheck: React.FC = () => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const { trans } = useTranslation()
  const location = useLocation()

  const {
    referrer = statusChecksUrls.base(),
    service,
    statusCheck
  } = (location.state as LocationState) ?? ({} as LocationState)

  const { isFetching: isFetchingStatusChecks, isError: hasErrorStatusChecks } =
    useStatusChecksContext()
  const {
    isCreatingAuthenticationOnly,
    setIsCreatingAuthenticationOnly,
    isFetchingAwsIAMRoleMetadata,
    hasErrorAwsIAMRoleMetadata,
    isEdit,
    setIsEdit,
    isOnboarding,
    setIsOnboarding
  } = useContext(ObservabilityToolAuthenticationContext)

  const isClone = Boolean(statusCheck)

  const isFetching = isFetchingStatusChecks || isFetchingAwsIAMRoleMetadata
  const isError = hasErrorStatusChecks || hasErrorAwsIAMRoleMetadata

  useEffect(() => {
    if (isCreatingAuthenticationOnly) {
      setIsCreatingAuthenticationOnly(false)
    }
  }, [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly])

  useEffect(() => {
    if (isEdit) {
      setIsEdit(false)
    }
  }, [isEdit, setIsEdit])

  /**
   * The isOnboarding flag is stored in the ObservabilityToolAuthenticationContext's state and is used to make rendering
   * decisions. Since this context is used in onboarding and non-onboarding workflows, we manually set the flag prior to
   * continuing.
   *
   * This workflow requires the isOnboarding flag value to be false.
   */
  useEffect(() => {
    if (isOnboarding) {
      setIsOnboarding(false)
    }
  }, [isOnboarding, setIsOnboarding])

  return (
    <>
      <Helmet
        title={
          isClone
            ? trans('status_checks.clone')
            : trans('status_checks.create_status_check.helmet.title')
        }
      />
      <Header>
        <StatusCheckBreadcrumbs
          endLabel={
            isClone
              ? trans('status_checks.clone')
              : trans(
                  'status_checks.create.create_status_check.breadcrumb.label'
                )
          }
          referrer={referrer}
          service={service}
        />
      </Header>
      <PageSection hasError={isError} isLoading={isFetching}>
        <PageContainer>
          <Stack mb={3} spacing={3}>
            <Typography data-test-create-status-check-title variant="h5">
              {trans('status_checks.create.create_status_check.header.title')}
            </Typography>
            <Typography data-test-create-status-check-description>
              {trans(
                'status_checks.create.create_status_check.header.description'
              )}
            </Typography>
          </Stack>
          {isClone ? (
            <EditStatusCheckFormContainer
              serviceName={service?.name}
              statusCheck={
                shouldFormatStatusCheck(statusCheck)
                  ? formatExistingStatusCheck(statusCheck)
                  : statusCheck
              }
            />
          ) : (
            <CreateStatusCheckForm service={service} />
          )}
        </PageContainer>
      </PageSection>
    </>
  )
}
